import { useEffect } from "react";

interface UseRecalculateMonthlyDataParams {
  lastMonthActiveUsersDuplicate: number;
  selectedYear: string;
  monthlyData: any;
  monthNames: string[];
  isMonthPassed: (monthStr: string) => boolean;
  calculateValues: (
    month: string,
    newValues: any,
    forcePreviousMonthActiveUsers?: number
  ) => any;
  LastData: any[];
  setMonthlyData: (data: any) => void;
}

export const useRecalculateMonthlyData = ({
  lastMonthActiveUsersDuplicate,
  selectedYear,
  monthlyData,
  monthNames,
  isMonthPassed,
  calculateValues,
  LastData,
  setMonthlyData,
}: UseRecalculateMonthlyDataParams) => {
  useEffect(() => {
    // Only recalculate, don't reinitialize from editableRows
    const recalculatedData = { ...monthlyData }; // Keep existing user edits

    for (let i = 0; i < monthNames.length; i++) {
      const month = monthNames[i];
      if (recalculatedData[month]) {
        let previousMonthActiveUsers;

        if (i === 0) {
          // January uses lastMonthActiveUsersDuplicate directly
          previousMonthActiveUsers = lastMonthActiveUsersDuplicate;
        } else {
          // Check if month has passed to determine which value to use
          const monthNum = i + 1;
          const paddedMonth = monthNum.toString().padStart(2, "0");
          const monthStr = `${selectedYear}-${paddedMonth}`;
          const isPassed = isMonthPassed(monthStr);

          if (isPassed) {
            // For passed months, use lastMonthActiveUsersDuplicate or LastData
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth();
            const currentDay = currentDate.getDate();
            const isEarlyQ3 = currentMonth === 6 && currentDay <= 15;

            if (isEarlyQ3 && i === 6) {
              // July
              const juneData = LastData.find((entry: any) => {
                const [year, month] = entry.month.split("-").map(Number);
                return month === 6 && year === Number(selectedYear);
              });
              previousMonthActiveUsers =
                juneData?.active_users || lastMonthActiveUsersDuplicate;
            } else {
              previousMonthActiveUsers = lastMonthActiveUsersDuplicate;
            }
          } else {
            // For future months: if the previous month is locked, use its
            // actual active_users from LastData (same as new forecast logic).
            // Otherwise cascade from the previous month's calculated activeUsers.
            const prevMonth = monthNames[i - 1];
            const prevMonthNum = i; // prevMonth is index i-1, its month number is i
            const prevMonthPadded = prevMonthNum.toString().padStart(2, "0");
            const prevMonthStr = `${selectedYear}-${prevMonthPadded}`;
            const isPrevPassed = isMonthPassed(prevMonthStr);

            if (isPrevPassed) {
              const prefix = `${selectedYear}-${prevMonthPadded}-`;
              const today = new Date();
              const currentYear = today.getFullYear();
              const currentMonthNum = today.getMonth() + 1;
              const isPrevCurrentMonth =
                Number(selectedYear) === currentYear && prevMonthNum === currentMonthNum;

              const actualEntry = isPrevCurrentMonth
                ? (LastData.find((e: any) => e.month?.startsWith(prefix) && e.month.includes("RR")) ??
                   LastData.find((e: any) => e.month?.startsWith(prefix)))
                : LastData.find((e: any) => e.month?.startsWith(prefix) && !e.month.includes("RR"));

              previousMonthActiveUsers =
                actualEntry?.active_users ??
                recalculatedData[prevMonth]?.activeUsers ??
                lastMonthActiveUsersDuplicate;
            } else {
              // If the previous month's quarter has started (mirrors CreateNew quarter-based
              // isMonthPassed), treat it as locked and use lastMonthActiveUsersDuplicate
              // so the first future month's oldActiveUsers matches the Create New calculation.
              const prevMonthIndex0 = prevMonthNum - 1; // 0-based
              const quarterStartMonth0 = Math.floor(prevMonthIndex0 / 3) * 3;
              const quarterStartDate = new Date(Number(selectedYear), quarterStartMonth0, 15);
              const isQuarterStarted = new Date() > quarterStartDate;
              previousMonthActiveUsers = isQuarterStarted
                ? lastMonthActiveUsersDuplicate
                : (recalculatedData[prevMonth]?.activeUsers ?? lastMonthActiveUsersDuplicate);
            }
          }
        }

        // Recalculate with existing data (preserves user edits)
        recalculatedData[month] = calculateValues(
          month,
          recalculatedData[month], // Use existing values, not editableRows
          previousMonthActiveUsers
        );
      }
    }

    setMonthlyData(recalculatedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMonthActiveUsersDuplicate, selectedYear]);
};
